import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import type { Product } from "../../../../types/product";
import { useCart } from "../../../../context/CartContext";
import { useShopifyProducts } from "../../../../context/ShopifyProductsContext";
import { useCompare } from "../../../../context/CompareContext";
import BundleSavePopover from "./BundleSavePopover";
import RecentlyComparedSection from "./RecentlyComparedSection";
import SocialProofBar from "./SocialProofBar";
import BackInStockModal from "./BackInStockModal";
import { useWishlist } from "../../../../context/WishlistContext";

interface Props {
  product: Product;
  onAddToCart: (qty: number) => void;
  added: boolean;
}

export default function ProductPurchasePanel({ product, onAddToCart, added }: Props) {
  const [qty, setQty] = useState(1);
  const [showBundle, setShowBundle] = useState(false);
  const [bundleDone, setBundleDone] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [heartPop, setHeartPop] = useState(false);
  const { isWishlisted, toggleWishlist, openWishlist } = useWishlist();
  const wishlisted = isWishlisted(product.id);

  const handleToggleWishlist = () => {
    const wasWishlisted = wishlisted;
    toggleWishlist({
      id: product.id,
      variantId: product.variantId,
      name: product.name,
      brand: product.brand,
      price: product.price,
      originalPrice: product.originalPrice,
      image: product.image,
      inStock: product.inStock,
      category: product.category,
    });
    if (!wasWishlisted) {
      setHeartPop(true);
      setTimeout(() => setHeartPop(false), 600);
    }
  };
  const { addItem } = useCart();
  const { products } = useShopifyProducts();
  const { isInCompare, toggleCompare, canAdd } = useCompare();

  // Pick best complementary product: opposite category, in-stock, closest price bracket
  const bundleProduct = useMemo<Product | null>(() => {
    const targetCat =
      product.category === "solar" ? "gas"
      : product.category === "gas" ? "solar"
      : "solar"; // whole-home → suggest solar portable

    const candidates = products.filter(
      (p) => p.category === targetCat && p.inStock && p.id !== product.id
    );
    if (!candidates.length) return null;

    // Prefer price range 30%–120% of current product
    const inRange = candidates.filter(
      (p) => p.price >= product.price * 0.3 && p.price <= product.price * 1.2
    );
    const pool = inRange.length ? inRange : candidates;

    // Pick highest-rated in pool
    return pool.reduce((best, cur) => (cur.rating > best.rating ? cur : best), pool[0]);
  }, [products, product]);

  const saveDollars = product.originalPrice ? product.originalPrice - product.price : null;
  const savePct = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : null;

  const trustItems = [
    { icon: "ri-truck-line", label: "Free Shipping", sub: "No minimums, ships fast", href: null },
    { icon: "ri-shield-check-line", label: "Manufacturer Warranty", sub: "View full warranty terms →", href: "/warranty" },
    { icon: "ri-arrow-go-back-line", label: "30-Day Returns", sub: "Hassle-free returns", href: "/return-policy" },
    { icon: "ri-customer-service-2-line", label: "Expert Support", sub: "(866) 605-4975", href: null },
  ];

  const handleAddToCart = () => {
    addItem(
      {
        id: product.id,
        variantId: product.variantId,
        name: product.name,
        brand: product.brand,
        price: product.price,
        image: product.image,
      },
      qty
    );
    onAddToCart(qty);

    // Only show bundle popover if we have a suggestion and haven't completed bundle yet
    if (bundleProduct && !bundleDone) {
      setShowBundle(true);
    }
  };

  const handleBundleAdded = () => {
    setShowBundle(false);
    setBundleDone(true);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Brand */}
      <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest">{product.brand}</p>

      {/* Title */}
      <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-snug">{product.name}</h1>

      {/* Rating */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <i
              key={s}
              className={`text-sm ${
                s <= Math.floor(product.rating) ? "ri-star-fill text-amber-400"
                : s - product.rating < 1 ? "ri-star-half-fill text-amber-400"
                : "ri-star-line text-gray-300"
              }`}
            ></i>
          ))}
        </div>
        <span className="text-sm text-gray-500">
          {product.rating.toFixed(1)} &nbsp;({product.reviews.toLocaleString()} reviews)
        </span>
      </div>

      {/* Price block */}
      <div className="bg-gray-50 rounded-xl p-5">
        {product.originalPrice ? (
          <>
            <div className="flex items-baseline gap-3 mb-1">
              <span className="text-3xl font-extrabold text-red-600">${product.price.toLocaleString()}</span>
              <span className="text-lg text-gray-400 line-through">${product.originalPrice.toLocaleString()}</span>
            </div>
            {saveDollars && savePct && (
              <div className="flex items-center gap-2">
                <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-md">
                  Save ${saveDollars.toLocaleString()} ({savePct}% off)
                </span>
              </div>
            )}
          </>
        ) : (
          <span className="text-3xl font-extrabold text-gray-900">${product.price.toLocaleString()}</span>
        )}

        <div className={`flex items-center gap-1.5 mt-3 text-sm font-medium ${product.inStock ? "text-emerald-600" : "text-red-500"}`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={product.inStock ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"}></i>
          </div>
          {product.inStock ? "In Stock — Ships within 1–2 business days" : "Currently Out of Stock"}
        </div>

        {product.inStock && (
          <div className="flex items-center gap-1.5 mt-1 text-sm text-emerald-600 font-medium">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-truck-line"></i>
            </div>
            Free Shipping — No order minimum
          </div>
        )}

        <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-bank-card-line text-amber-500"></i>
          </div>
          As low as <span className="font-semibold text-gray-700 mx-1">${Math.round(product.price / 24)}/mo</span> with 0% APR financing
        </div>
      </div>

      {/* Social proof urgency signals */}
      <SocialProofBar product={product} />

      {/* Highlights */}
      {product.highlights.length > 0 && (
        <ul className="space-y-1.5">
          {product.highlights.map((h) => (
            <li key={h} className="flex items-start gap-2 text-sm text-gray-600">
              <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-check-line text-emerald-500"></i>
              </div>
              {h}
            </li>
          ))}
        </ul>
      )}

      {/* Bundle done confirmation strip */}
      {bundleDone && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
          <div className="w-5 h-5 flex items-center justify-center text-emerald-500 shrink-0">
            <i className="ri-checkbox-circle-fill"></i>
          </div>
          <p className="text-sm text-emerald-700 font-semibold">Bundle added! Both items are in your cart.</p>
        </div>
      )}

      {/* Bundle teaser (shown if not yet bundled and product available) */}
      {!bundleDone && bundleProduct && product.inStock && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div className="w-5 h-5 flex items-center justify-center text-amber-500 shrink-0">
            <i className="ri-gift-line"></i>
          </div>
          <p className="text-sm text-amber-700">
            <span className="font-bold">Bundle &amp; Save:</span> Add a complementary{" "}
            {bundleProduct.category === "solar" ? "solar generator" : bundleProduct.category === "gas" ? "gas generator" : "backup system"}{" "}
            and get <span className="font-bold">10% off</span> — offered after you add to cart.
          </p>
        </div>
      )}

      {/* Qty + Add to Cart */}
      {product.inStock && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">Quantity</label>
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-10 h-10 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer text-lg"
              >
                <i className="ri-subtract-line"></i>
              </button>
              <span className="w-12 text-center font-bold text-gray-900 text-sm select-none">{qty}</span>
              <button
                onClick={() => setQty(Math.min(10, qty + 1))}
                className="w-10 h-10 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer text-lg"
              >
                <i className="ri-add-line"></i>
              </button>
            </div>
          </div>

          <button
            onClick={handleAddToCart}
            className={`w-full py-4 rounded-xl font-bold text-base transition-all cursor-pointer flex items-center justify-center gap-2 ${
              added
                ? "bg-emerald-500 text-white"
                : "bg-amber-500 hover:bg-amber-400 text-white"
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={added ? "ri-check-line" : "ri-shopping-cart-line"}></i>
            </div>
            {added ? "Added to Cart!" : "Add to Cart"}
          </button>

          <a
            href="tel:8666054975"
            className="w-full py-3.5 rounded-xl border-2 border-gray-200 hover:border-amber-400 text-gray-700 hover:text-amber-600 font-semibold text-sm transition-all cursor-pointer flex items-center justify-center gap-2"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line"></i>
            </div>
            Call to Order: (866) 605-4975
          </a>
        </div>
      )}

      {!product.inStock && (
        <div className="flex flex-col gap-3">
          {/* Out-of-stock notice */}
          <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
            <div className="w-5 h-5 flex items-center justify-center shrink-0 text-red-400 mt-0.5">
              <i className="ri-error-warning-line"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-red-700">Currently Out of Stock</p>
              <p className="text-xs text-red-500 mt-0.5">
                High demand — sign up below to be first in line when inventory returns.
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowNotifyModal(true)}
            className="w-full py-4 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-bold text-base cursor-pointer transition-colors flex items-center justify-center gap-2.5 whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-notification-3-fill text-amber-400"></i>
            </div>
            Notify Me When Back in Stock
          </button>

          <a
            href="tel:8666054975"
            className="w-full py-3.5 rounded-xl border-2 border-gray-200 hover:border-amber-400 text-gray-700 hover:text-amber-600 font-semibold text-sm transition-all cursor-pointer flex items-center justify-center gap-2"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line"></i>
            </div>
            Call to Check Availability: (866) 605-4975
          </a>
        </div>
      )}

      {/* Back-in-stock email alert modal */}
      <BackInStockModal
        isOpen={showNotifyModal}
        onClose={() => setShowNotifyModal(false)}
        productName={product.name}
        productId={product.id}
      />

      {/* Wishlist toggle */}
      <button
        onClick={handleToggleWishlist}
        className={`w-full py-3 rounded-xl border-2 font-semibold text-sm transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer ${
          wishlisted
            ? "border-rose-400 bg-rose-50 text-rose-600 hover:border-rose-500"
            : "border-gray-200 text-gray-500 hover:border-rose-300 hover:text-rose-500 hover:bg-rose-50/50"
        } ${heartPop ? "scale-[1.02]" : "scale-100"}`}
        style={{ transition: "transform 0.2s ease, border-color 0.15s, color 0.15s, background-color 0.15s" }}
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className={wishlisted ? "ri-heart-fill text-rose-500" : "ri-heart-line"}></i>
        </div>
        {wishlisted ? (
          <span>
            Saved to Wishlist &mdash;{" "}
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); openWishlist(); }}
              onKeyDown={(e) => e.key === "Enter" && openWishlist()}
              className="underline text-rose-500 cursor-pointer"
            >
              View
            </span>
          </span>
        ) : (
          "Save to Wishlist"
        )}
      </button>

      {/* Compare toggle — available regardless of stock status */}
      {(() => {
        const inCompare = isInCompare(product.id);
        const disabled = !inCompare && !canAdd;
        return (
          <button
            onClick={() => !disabled && toggleCompare(product)}
            disabled={disabled}
            className={`w-full py-3 rounded-xl border-2 font-semibold text-sm transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
              inCompare
                ? "border-amber-400 bg-amber-50 text-amber-700 hover:border-amber-500 cursor-pointer"
                : disabled
                ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed"
                : "border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600 cursor-pointer"
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={inCompare ? "ri-check-line text-amber-500" : "ri-scales-3-line"}></i>
            </div>
            {inCompare
              ? "Added to Compare — Remove"
              : disabled
              ? "Compare tray full (max 3)"
              : "Add to Compare"}
          </button>
        );
      })()}

      {/* Trust badges */}
      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
        {trustItems.map((t) => {
          const inner = (
            <>
              <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                <i className={`${t.icon} text-amber-500`}></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-800 leading-tight">{t.label}</p>
                <p className={`text-xs ${t.href ? "text-amber-500" : "text-gray-400"}`}>{t.sub}</p>
              </div>
            </>
          );
          return t.href ? (
            <Link
              key={t.label}
              to={t.href}
              className="flex items-start gap-2 hover:opacity-80 transition-opacity cursor-pointer"
            >
              {inner}
            </Link>
          ) : (
            <div key={t.label} className="flex items-start gap-2">
              {inner}
            </div>
          );
        })}
      </div>

      {/* Share */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-400">Share:</span>
        {["ri-facebook-fill", "ri-twitter-x-line", "ri-mail-line", "ri-link"].map((icon) => (
          <button key={icon} className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:text-amber-500 hover:border-amber-300 transition-colors cursor-pointer">
            <i className={`${icon} text-sm`}></i>
          </button>
        ))}
      </div>

      {/* Recently Compared */}
      <RecentlyComparedSection />

      {/* Bundle & Save Popover */}
      <BundleSavePopover
        currentProduct={product}
        bundleProduct={bundleProduct}
        isOpen={showBundle}
        onClose={() => setShowBundle(false)}
        onBundleAdded={handleBundleAdded}
      />
    </div>
  );
}
